import apiService from "@/lib/api";

const PLATFORM_BASE = "/api/v2/platform";
const ADMIN_BASE = "/api/v2/admin";

/**
 * Module entitlement (per tenant) and platform availability (fleet-wide).
 *
 * Two separate endpoints for two separate axes, and they must stay that way:
 *
 * - `getTenantModules` / `updateTenantModules` — "what is in their plan".
 *   Permanent, per tenant.
 * - `getPlatformModules` / `updatePlatformModules` — "is this working right
 *   now". Temporary, fleet-wide, and it NEVER touches anyone's entitlement.
 *
 * That separation is why re-enabling a killed module is automatic: nobody's
 * plan was disturbed, so everyone who had it gets it back with no per-tenant
 * action and nothing for the SuperAdmin to remember.
 */
export const ModuleService = {
  /**
   * The module registry itself — the catalogue, with no tenant state.
   *
   * Names, descriptions, the parent links and the three flags all come from
   * the server. The client keeps codes only, so the two halves cannot drift.
   * SuperAdmin only: a tenant has no reason to receive the list of everything
   * it could have bought.
   */
  getModuleRegistry: async () => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/modules/registry`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching the module registry:", error);
      throw error;
    }
  },

  /** Fleet-wide availability, for every module that has a kill switch. */
  getPlatformModules: async () => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/modules`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching platform modules:", error);
      throw error;
    }
  },

  /**
   * Turn modules on/off for every tenant at once.
   *
   * Partial — send only what is changing.
   * @param {Object} modules e.g. `{ "ai.interview_questions": false }`
   */
  updatePlatformModules: async (modules) => {
    try {
      const response = await apiService.putSecure(`${PLATFORM_BASE}/modules`, {
        modules,
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while updating platform modules:", error);
      throw error;
    }
  },

  /**
   * One tenant's entitlements, with each module's current fleet-wide
   * availability alongside — so the UI can show "they own it but it is dark
   * right now" rather than collapsing the two into one state.
   */
  getTenantModules: async (tenantId) => {
    try {
      const response = await apiService.getSecure(`${ADMIN_BASE}/tenants/${tenantId}/modules`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching tenant modules:", error);
      throw error;
    }
  },

  /**
   * Change what one tenant's plan includes. Partial.
   *
   * Takes effect on that tenant's very next request — permissions are
   * re-resolved from the DB per request, so nobody has to re-login.
   */
  updateTenantModules: async (tenantId, modules) => {
    try {
      const response = await apiService.putSecure(`${ADMIN_BASE}/tenants/${tenantId}/modules`, { modules });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while updating tenant modules:", error);
      throw error;
    }
  },
};

export default ModuleService;
